import {
  GetForm_vm_form_formItems as FormItemType,
  GetForm_vm_form_formItems_VOLUNTEER_FormItemFieldType as FieldFormItemType,
} from '../__generated__/GetForm';

export type FormItemsMapById = { [key: string]: FormItemType };
export type FormItemsByTrigger = {
  [key: string]: {
    formItemId: string;
    triggerItemIds: string[];
    taskFieldId: string;
    optionType: string;
  };
};

export type FormItemByTriggerOption = {
  [key: string]: string;
};

export type FormItemsByGroup = {
  [groupFormItemId: string]: string[];
};

export type FormItemByGroupItem = {
  [itemId: string]: string;
};

export type FormItemsDeserialize = {
  allIds: string[];
  byId: FormItemsMapById;
  byTrigger: FormItemsByTrigger;
  byTriggerOption: FormItemByTriggerOption;
  byGroup: FormItemsByGroup;
  byGroupItem: FormItemByGroupItem;
};

export function deserializeFormItems(unfilteredFormItems?: FormItemType[]): FormItemsDeserialize {
  // An item whose type the query did not select comes back as __typename only.
  // It cannot be rendered or saved, so drop it rather than keying it as undefined.
  const formItems = unfilteredFormItems?.filter((formItem) => Boolean(formItem?.formItemId));

  if (!formItems || formItems.length === 0) {
    return {
      allIds: [],
      byId: {},
      byTrigger: {},
      byTriggerOption: {},
      byGroup: {},
      byGroupItem: {},
    };
  }

  // Build byTriggerOption first to identify which items are triggered
  const byTriggerOption = formItems.reduce((acc, formItem) => {
    const triggerOption: any = (formItem as any).triggerItem?.trigger?.triggerOption;

    if (triggerOption) {
      const optionId =
        triggerOption.sourceOptionValue ||
        triggerOption.quizTaskFieldOptionId ||
        triggerOption.dropdownFieldOptionId ||
        triggerOption.checkboxFieldOptionId ||
        triggerOption.multipleChoiceFieldOptionId;
      return {
        ...acc,
        [formItem.formItemId]: optionId,
      };
    }

    return acc;
  }, {} as FormItemByTriggerOption);

  // Build byGroup and byGroupItem FIRST so we can exclude group children from allIds
  const byGroup: FormItemsByGroup = {};
  const byGroupItem: FormItemByGroupItem = {};
  const groupChildById: FormItemsMapById = {};

  formItems.forEach((formItem) => {
    if (formItem.__typename === 'VOLUNTEER_FormItemRepeatableGroupType') {
      const groupItems = ((formItem as any).groupItems || []) as FormItemType[];
      const childIds = groupItems
        .sort((a: any, b: any) => ((a.order ?? 0) > (b.order ?? 0) ? 1 : -1))
        .map((child) => child.formItemId);
      byGroup[formItem.formItemId] = childIds;
      groupItems.forEach((child) => {
        byGroupItem[child.formItemId] = formItem.formItemId;
        groupChildById[child.formItemId] = child;
      });
    }
  });

  // allIds only includes top-level items (not triggered items, not group children)
  const allIds = formItems
    .filter((formItem) => !byTriggerOption[formItem.formItemId] && !byGroupItem[formItem.formItemId])
    .map((formItem) => formItem.formItemId);

  return {
    allIds,
    byId: {
      ...formItems.reduce(
        (acc, formItem) => ({
          ...acc,
          [formItem.formItemId]: formItem,
        }),
        {} as FormItemsMapById
      ),
      ...groupChildById,
    },
    byGroup,
    byGroupItem,
    byTrigger: [...formItems, ...Object.values(groupChildById)].reduce((acc, formItem) => {
      if (formItem.__typename !== 'VOLUNTEER_FormItemFieldType') {
        return acc;
      }

      const fieldItem = formItem as FieldFormItemType;
      let newTriggerMap = {} as FormItemsByTrigger;

      if (fieldItem.field.__typename === 'VOLUNTEER_FormCheckboxTaskFieldType') {
        const options = fieldItem.field.checkboxOptions || [];
        newTriggerMap = options.reduce(
          (triggerMap, checkboxOption) => ({
            ...triggerMap,
            [checkboxOption.checkboxFieldOptionId]: {
              triggerItemIds: checkboxOption.trigger?.items.map((item) => item.item?.formItemId).filter(Boolean) || [],
              formItemId: formItem.formItemId,
              taskFieldId: fieldItem.field.taskFieldId,
              optionType: 'checkbox',
            },
          }),
          {}
        );
      }

      if (fieldItem.field.__typename === 'VOLUNTEER_FormDropdownTaskFieldType') {
        const options = fieldItem.field.dropdownOptions || [];
        newTriggerMap = options.reduce(
          (triggerMap, dropdownOption) => ({
            ...triggerMap,
            [dropdownOption.dropdownFieldOptionId]: {
              triggerItemIds: dropdownOption.trigger?.items.map((item) => item.item?.formItemId).filter(Boolean) || [],
              formItemId: formItem.formItemId,
              taskFieldId: fieldItem.field.taskFieldId,
              optionType: 'dropdown',
            },
          }),
          {}
        );
      }

      if (fieldItem.field.__typename === 'VOLUNTEER_FormMultipleChoiceTaskFieldType') {
        const options = fieldItem.field.multipleChoiceOptions || [];
        newTriggerMap = options.reduce(
          (triggerMap, multipleChoiceOption) => ({
            ...triggerMap,
            [multipleChoiceOption.multipleChoiceFieldOptionId]: {
              triggerItemIds: multipleChoiceOption.trigger?.items.map((item) => item.item?.formItemId).filter(Boolean) || [],
              formItemId: formItem.formItemId,
              taskFieldId: fieldItem.field.taskFieldId,
              optionType: 'multipleChoice',
            },
          }),
          {}
        );
      }

      if (fieldItem.field.__typename === 'VOLUNTEER_FormQuizTaskFieldType') {
        const options = fieldItem.field.quizTaskFieldOptions || [];
        newTriggerMap = options.reduce(
          (triggerMap, quizOption) => ({
            ...triggerMap,
            [quizOption.quizTaskFieldOptionId]: {
              triggerItemIds: quizOption.trigger?.items.map((item) => item.item?.formItemId).filter(Boolean) || [],
              formItemId: formItem.formItemId,
              taskFieldId: fieldItem.field.taskFieldId,
              optionType: 'quiz',
            },
          }),
          {}
        );
      }

      if (fieldItem.field.__typename === 'VOLUNTEER_CrossTaskDynamicTaskFieldType') {
        newTriggerMap = (fieldItem.field.crossTaskDynamicOptions || []).reduce(
          (triggerMap, crossTaskOption) => ({
            ...triggerMap,
            [crossTaskOption.sourceOptionValue]: {
              triggerItemIds: crossTaskOption.trigger?.items?.map((item) => item.item?.formItemId).filter(Boolean) || [],
              formItemId: formItem.formItemId,
              taskFieldId: fieldItem.field.taskFieldId,
              optionType: 'crossTaskDynamic',
            },
          }),
          {}
        );
      }

      return { ...acc, ...newTriggerMap };
    }, {} as FormItemsByTrigger),
    byTriggerOption,
  };
}
